// PlantGeneration.jsx
// TK 7/12/2k22

import React, { useState, useEffect, useMemo, useRef } from "react";
import { collection, addDoc } from "firebase/firestore";
import db from "../../Firebase";
import { averyTemplates } from "../../constants";

// Components
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// CSS
import "../PlantEditor/PlantEditor.css";

const API_BASE = "https://us-central1-plants-5f51e.cloudfunctions.net/api";

// Common species options (you can expand this list)
const commonSpecies = [
  { value: "Ficus lyrata", label: "Ficus Lyrata (Fiddle Leaf Fig)" },
  {
    value: "Dracaena trifasciata",
    label: "Dracaena Trifasciata (Snake Plant)",
  },
  {
    value: "Pilea peperomioides",
    label: "Pilea Peperomioides (Chinese Money Plant)",
  },
  { value: "Monstera deliciosa", label: "Monstera Deliciosa" },
  { value: "Calathea lancifolia", label: "Calathea Lancifolia" },
  { value: "Epipremnum aureum", label: "Pothos (Epipremnum aureum)" },
  { value: "Philodendron hederaceum", label: "Philodendron Hederaceum" },
  { value: "Spathiphyllum wallisii", label: "Peace Lily (Spathiphyllum wallisii)" },
  {
    value: "Zamioculcas zamiifolia",
    label: "ZZ Plant (Zamioculcas zamiifolia)",
  },
];

const CRAFT_FAIR_PRESET = [
  ["Epipremnum aureum", 16],
  ["Monstera deliciosa", 16],
  ["Calathea lancifolia", 16],
  ["Dracaena trifasciata", 16],
  ["Philodendron hederaceum", 16],
  ["Spathiphyllum wallisii", 16],
];

const PlantGeneration = () => {
  const [species, setSpecies] = useState("");
  const [plantName, setPlantName] = useState("");
  const [availableSpecies, setAvailableSpecies] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Batch generation state
  const [batchCount, setBatchCount] = useState(10);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState("");
  const [batchResult, setBatchResult] = useState(null);
  const [campaignEnabled, setCampaignEnabled] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const printItemCounterRef = useRef(0);
  const createPrintItem = (species = "", count = 16) => ({
    id: `print-item-${printItemCounterRef.current++}`,
    species,
    count,
  });
  const [printJobItems, setPrintJobItems] = useState(() => [createPrintItem()]);
  const [printJobCampaign, setPrintJobCampaign] = useState("");
  const [printJobTemplate, setPrintJobTemplate] = useState(1);
  const [printJobLoading, setPrintJobLoading] = useState(false);
  const [printJobError, setPrintJobError] = useState("");
  const [printJobResult, setPrintJobResult] = useState(null);

  useEffect(() => {
    setAvailableSpecies(commonSpecies);
  }, []);

  const printJobTotals = useMemo(() => {
    const totalTags = printJobItems.reduce((sum, item) => {
      const count = parseInt(item.count, 10);
      return sum + (Number.isFinite(count) ? count : 0);
    }, 0);
    const template = averyTemplates[printJobTemplate];
    const labelsPerSheet = template.rows * template.columns;
    return {
      totalTags,
      labelsPerSheet,
      sheetCount: labelsPerSheet ? Math.ceil(totalTags / labelsPerSheet) : 0,
    };
  }, [printJobItems, printJobTemplate]);

  const createPlant = async () => {
    if (!species.trim()) {
      setError("Please select or enter a species");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      // Step 1: Generate the plant from the backend
      const url = new URL(`${API_BASE}/newPlant`);
      url.searchParams.append("species", species);
      if (plantName && plantName.trim()) {
        url.searchParams.append("name", plantName.trim());
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const plantData = await response.json();

      // Step 2: Save the plant to Firestore and get the ID
      const docRef = await addDoc(collection(db, "Plants"), plantData);

      // Step 3: Add the ID to the plant data for display
      const plantWithId = {
        ...plantData,
        id: docRef.id,
      };

      setResult(plantWithId);
    } catch (err) {
      setError(`Failed to create plant: ${err.message}`);
      console.error("Error creating plant:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSpecies("");
    setPlantName("");
    setResult(null);
    setError("");
  };

  const generateBatch = async () => {
    if (!species.trim()) {
      setBatchError("Please select or enter a species");
      return;
    }
    if (!batchCount || batchCount < 1) {
      setBatchError("Enter a valid batch size (>= 1)");
      return;
    }

    setBatchLoading(true);
    setBatchError("");
    setBatchResult(null);

    try {
      const response = await fetch(`${API_BASE}/generateBatch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          species: species.trim(),
          count: Number(batchCount),
          campaign:
            campaignEnabled && campaignName.trim()
              ? campaignName.trim()
              : undefined,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setBatchResult(data);
    } catch (err) {
      console.error("Error generating batch:", err);
      setBatchError(`Failed to generate batch: ${err.message}`);
    } finally {
      setBatchLoading(false);
    }
  };

  const updatePrintItem = (id, field, value) => {
    setPrintJobItems((items) =>
      items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const addPrintItem = () => {
    setPrintJobItems((items) => [...items, createPrintItem()]);
  };

  const removePrintItem = (id) => {
    setPrintJobItems((items) => (items.length > 1 ? items.filter((item) => item.id !== id) : items));
  };

  const loadCraftFairPreset = () => {
    setPrintJobItems(CRAFT_FAIR_PRESET.map(([speciesName, count]) => createPrintItem(speciesName, count)));
    setPrintJobTemplate(1);
  };

  const generatePrintJob = async () => {
    const items = printJobItems
      .map((item) => ({
        species: item.species.trim(),
        count: Number(item.count),
      }))
      .filter((item) => item.species && item.count > 0);

    if (items.length === 0) {
      setPrintJobError("Add at least one species with a positive tag count.");
      return;
    }
    if (!printJobCampaign.trim()) {
      setPrintJobError("Enter a campaign name so scans can be attributed.");
      return;
    }

    setPrintJobLoading(true);
    setPrintJobError("");
    setPrintJobResult(null);

    try {
      const template = averyTemplates[printJobTemplate];
      const response = await fetch(`${API_BASE}/generatePrintJob`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items,
          campaign: printJobCampaign.trim(),
          campaignLabel: printJobCampaign.trim(),
          templateId: template.id,
          labelsPerSheet: template.rows * template.columns,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setPrintJobResult(data);
    } catch (err) {
      console.error("Error generating print job:", err);
      setPrintJobError(`Failed to generate print job: ${err.message}`);
    } finally {
      setPrintJobLoading(false);
    }
  };

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Plant Generation Station</div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        <div className="plantFieldContainer">
          <label htmlFor="speciesSelect" className="plantTextFieldLabel">
            Select Species
          </label>
          <select
            name="speciesSelect"
            id="speciesSelect"
            className="textbox plantIDTextbox"
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            style={{ height: "40px" }}
          >
            <option value="">-- Select a Species --</option>
            {availableSpecies.map((spec, index) => (
              <option value={spec.value} key={`species-${index}`}>
                {spec.label}
              </option>
            ))}
          </select>
        </div>

        <div className="plantFieldContainer">
          <label htmlFor="customSpecies" className="plantTextFieldLabel">
            Or Enter Custom Species (Genus species format)
          </label>
          <input
            className="textbox plantIDTextbox"
            placeholder="e.g., Ficus elastica, Aloe vera"
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            type="text"
            id="customSpecies"
          />
        </div>

        <div className="plantFieldContainer">
          <label htmlFor="plantName" className="plantTextFieldLabel">
            Plant Name (Optional)
          </label>
          <input
            className="textbox plantIDTextbox"
            placeholder="Enter a name for your plant (leave blank for unnamed)"
            value={plantName}
            onChange={(e) => setPlantName(e.target.value)}
            type="text"
            id="plantName"
          />
        </div>

        <button
          type="button"
          className="getPlantButton"
          onClick={createPlant}
          disabled={loading || !species.trim()}
          style={{
            opacity: loading || !species.trim() ? 0.6 : 1,
            cursor: loading || !species.trim() ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Creating Plant..." : "Create New Plant"}
        </button>
        <div style={{ marginTop: "12px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={campaignEnabled}
              onChange={(e) => setCampaignEnabled(e.target.checked)}
            />
            Include campaign param
          </label>
          {campaignEnabled && (
            <input
              className="textbox plantIDTextbox"
              placeholder="campaign name (e.g., spring-fair)"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              type="text"
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <button
          type="button"
          className="getPlantButton"
          onClick={resetForm}
          style={{
            backgroundColor: "#6c757d",
            marginTop: "10px",
          }}
        >
          Reset Form
        </button>

        {error && (
          <div
            style={{
              color: "red",
              textAlign: "center",
              marginTop: "20px",
              padding: "10px",
              backgroundColor: "#ffe6e6",
              borderRadius: "5px",
            }}
          >
            {error}
          </div>
        )}

        {result && (
          <div
            style={{
              marginTop: "30px",
              padding: "20px",
              backgroundColor: "#e8f5e8",
              borderRadius: "10px",
              border: "2px solid #4caf50",
            }}
          >
            <h3 style={{ color: "#2e7d32", marginTop: 0 }}>
              Plant Created Successfully! 🌱
            </h3>

            <div style={{ marginBottom: "15px" }}>
              <strong>Plant ID:</strong>
              <code
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  marginLeft: "8px",
                }}
              >
                {result.id}
              </code>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <strong>Name:</strong> {result.name || "Unnamed"}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <strong>Species:</strong> {result.species}
            </div>

            {result.plantsonality && (
              <div style={{ marginBottom: "15px" }}>
                <strong>Personality Traits:</strong>
                <ul style={{ marginTop: "5px" }}>
                  {Object.entries(result.plantsonality.traits).map(
                    ([trait, value]) => (
                      <li key={trait}>
                        {trait.charAt(0).toUpperCase() + trait.slice(1)}:{" "}
                        {value}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

            <div style={{ marginTop: "20px" }}>
              <a
                href={`http://localhost:3000/chat/${result.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  backgroundColor: "#4caf50",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "5px",
                  marginRight: "10px",
                }}
              >
                💬 Open Chat for this Plant
              </a>

              <a
                href={`http://localhost:3000/plant/${result.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  backgroundColor: "#2196f3",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "5px",
                }}
              >
                📋 View Plant Bio Page
              </a>
            </div>
          </div>
        )}

        {/* Batch Generation Section */}
        <hr style={{ margin: "30px 0" }} />
        <h3 style={{ marginTop: 0 }}>Campaign Print Job</h3>
        <p style={{ marginTop: 0 }}>
          Mix multiple species into one campaign and export multi-page sticker sheets.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <button
            type="button"
            className="getPlantButton"
            onClick={loadCraftFairPreset}
            style={{ backgroundColor: "#7a4f2d" }}
          >
            Load Crafts Fair Preset
          </button>
          <button
            type="button"
            className="getPlantButton"
            onClick={addPrintItem}
            style={{ backgroundColor: "#4f7a57" }}
          >
            Add Species Row
          </button>
        </div>
        <div className="plantFieldContainer">
          <label htmlFor="printJobCampaign" className="plantTextFieldLabel">
            Campaign Name
          </label>
          <input
            className="textbox plantIDTextbox"
            id="printJobCampaign"
            type="text"
            placeholder="e.g., crafts-fair-summer-2026"
            value={printJobCampaign}
            onChange={(e) => setPrintJobCampaign(e.target.value)}
          />
        </div>
        <div className="plantFieldContainer">
          <label htmlFor="printJobTemplate" className="plantTextFieldLabel">
            Sticker Sheet Template
          </label>
          <select
            className="textbox plantIDTextbox"
            id="printJobTemplate"
            value={printJobTemplate}
            onChange={(e) => setPrintJobTemplate(Number(e.target.value))}
            style={{ height: "40px" }}
          >
            {averyTemplates.map((template, index) => (
              <option value={index} key={template.id}>
                {template.id} ({template.rows * template.columns} labels per sheet)
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          {printJobItems.map((item, index) => (
            <div
              key={item.id}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) 110px 110px",
                gap: 12,
                alignItems: "end",
              }}
            >
              <div>
                <label className="plantTextFieldLabel">Species {index + 1}</label>
                <select
                  className="textbox plantIDTextbox"
                  value={item.species}
                  onChange={(e) => updatePrintItem(item.id, "species", e.target.value)}
                  style={{ height: "40px" }}
                >
                  <option value="">-- Select a Species --</option>
                  {availableSpecies.map((spec) => (
                    <option value={spec.value} key={`${item.id}-${spec.value}`}>
                      {spec.label}
                    </option>
                  ))}
                </select>
                <input
                  className="textbox plantIDTextbox"
                  placeholder="Or enter custom species"
                  value={item.species}
                  onChange={(e) => updatePrintItem(item.id, "species", e.target.value)}
                  type="text"
                  style={{ marginTop: 8 }}
                />
              </div>
              <div>
                <label className="plantTextFieldLabel">Count</label>
                <input
                  className="textbox plantIDTextbox"
                  type="number"
                  min={1}
                  value={item.count}
                  onChange={(e) => updatePrintItem(item.id, "count", e.target.value)}
                />
              </div>
              <button
                type="button"
                className="getPlantButton"
                onClick={() => removePrintItem(item.id)}
                disabled={printJobItems.length === 1}
                style={{
                  backgroundColor: "#9b3d3d",
                  opacity: printJobItems.length === 1 ? 0.6 : 1,
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: "#f5efe4",
            borderRadius: 8,
          }}
        >
          <strong>Total tags:</strong> {printJobTotals.totalTags} · <strong>Sheets:</strong>{" "}
          {printJobTotals.sheetCount} · <strong>Labels per sheet:</strong>{" "}
          {printJobTotals.labelsPerSheet}
        </div>
        <button
          type="button"
          className="getPlantButton"
          onClick={generatePrintJob}
          disabled={printJobLoading}
          style={{
            opacity: printJobLoading ? 0.6 : 1,
            cursor: printJobLoading ? "not-allowed" : "pointer",
            backgroundColor: "#c86b2d",
            marginTop: "16px",
          }}
        >
          {printJobLoading ? "Generating Print Job..." : "Generate Campaign Print Job"}
        </button>
        {printJobError && (
          <div
            style={{
              color: "red",
              textAlign: "center",
              marginTop: "16px",
              padding: "10px",
              backgroundColor: "#ffe6e6",
              borderRadius: "5px",
            }}
          >
            {printJobError}
          </div>
        )}
        {printJobResult && (
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "#fff2e6",
              borderRadius: "8px",
              border: "1px solid #e8a66a",
            }}
          >
            <div style={{ marginBottom: "8px" }}>
              <strong>Print Job ID:</strong>
              <code style={{ marginLeft: 8 }}>{printJobResult.printJobID}</code>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <strong>Campaign:</strong> {printJobResult.campaignLabel || printJobResult.campaign}
            </div>
            <div style={{ marginBottom: "8px" }}>
              <strong>Total tags:</strong> {printJobResult.totalCount} · <strong>Sheets:</strong>{" "}
              {printJobResult.sheetCount}
            </div>
            <div style={{ marginBottom: "12px" }}>
              <strong>Species mix:</strong>{" "}
              {printJobResult.requestedItems
                .map((item) => `${item.species} x ${item.count}`)
                .join(", ")}
            </div>
            <div>
              <a
                href={`/console/taggeneration?printJob=${printJobResult.printJobID}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "8px 14px",
                  backgroundColor: "#b85c1c",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "4px",
                }}
              >
                Open Multi-Sheet Tag Generator
              </a>
            </div>
          </div>
        )}

        <hr style={{ margin: "30px 0" }} />
        <h3 style={{ marginTop: 0 }}>Batch Generation</h3>
        <div className="plantFieldContainer">
          <label htmlFor="batchCount" className="plantTextFieldLabel">
            Number of Plants
          </label>
          <input
            className="textbox plantIDTextbox"
            id="batchCount"
            type="number"
            min={1}
            max={200}
            value={batchCount}
            onChange={(e) => setBatchCount(e.target.value)}
            style={{ width: "120px" }}
          />
        </div>
        <button
          type="button"
          className="getPlantButton"
          onClick={generateBatch}
          disabled={batchLoading || !species.trim()}
          style={{
            opacity: batchLoading || !species.trim() ? 0.6 : 1,
            cursor: batchLoading || !species.trim() ? "not-allowed" : "pointer",
            backgroundColor: "#8e44ad",
          }}
        >
          {batchLoading ? "Generating Batch..." : "Generate Batch for Species"}
        </button>
        {batchError && (
          <div
            style={{
              color: "red",
              textAlign: "center",
              marginTop: "16px",
              padding: "10px",
              backgroundColor: "#ffe6e6",
              borderRadius: "5px",
            }}
          >
            {batchError}
          </div>
        )}
        {batchResult && (
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "#eef5ff",
              borderRadius: "8px",
              border: "1px solid #90caf9",
            }}
          >
            <div style={{ marginBottom: "8px" }}>
              <strong>Batch ID:</strong>
              <code style={{ marginLeft: 8 }}>{batchResult.batchID}</code>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <strong>Species:</strong> {batchResult.species} · <strong>Count:</strong> {batchResult.count}
              {batchResult.campaign && (
                <>
                  {" "}· <strong>Campaign:</strong> {batchResult.campaign}
                </>
              )}
            </div>
            <div>
              <a
                href={`/console/taggeneration`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "8px 14px",
                  backgroundColor: "#1976d2",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "4px",
                }}
              >
                Open Tag Generator (paste Batch ID)
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlantGeneration;
